// Multi-manager buyer teams (WS4) — the team's lead rollups.
//
// 🔴 EVERY NUMBER HERE IS ALREADY ON THE DETAIL PAYLOAD. No new endpoint, no
// second aggregation: a rollup computed twice is a rollup free to disagree
// with itself, and this tab is a readout, not a source.
//
// 🔴 THE TWO FAMILIES DO NOT RECONCILE, ON PURPOSE. Leads follow the LEAD;
// members and the buyers they hold follow the PERSON (D2). A specialist on
// this team holding a lead that belongs to another team is counted under
// "buyers held" and not under "leads", and the reverse happens too. The tiles
// are grouped and captioned accordingly rather than being laid out as one row
// that invites the subtraction.

import { Link } from "react-router-dom";
import type { BuyerTeamDetailDto } from "@/api/buyerTeams";
import { Card, StatCard, StatsBar } from "@/design-system/components/ui";

/** D12 — where the per-specialist board for this team lives (WS-B4). */
export function teamBoardPath(teamId: string): string {
  return `/buyers/reports?tab=specialists&team=${encodeURIComponent(teamId)}`;
}

export function PerformanceTab({ team }: { team: BuyerTeamDetailDto }) {
  const owned = team.leadCount - team.unassignedLeadCount;

  return (
    <div className="flex flex-col gap-space-4" data-testid="buyer-team-performance-tab">
      {/* D12 — the rollups here are the team's totals; the PER-SPECIALIST
          view (held, entitlement, overdue, stale, this month's qualified and
          converted) is the reports board, linked rather than rebuilt. */}
      <p className="text-small text-gray-mid m-0">
        Per-consultant standings, overdue follow-ups and stale buyers are on the team board.{" "}
        <Link
          to={teamBoardPath(team.id)}
          className="text-gold-shadow hover:underline"
          data-testid="buyer-team-open-board"
        >
          Open the team board
        </Link>
      </p>
      <Card>
        <h2 className="font-display text-h3 text-onyx m-0">Leads routed to this team</h2>
        <p className="text-caption text-gray-mid m-0 mt-space-1 mb-space-3 max-w-reading">
          Counted by the lead, archived excluded. A lead stays on this team&apos;s books
          whichever consultant ends up carrying it.
        </p>
        <StatsBar columns={3}>
          <StatCard label="Leads" value={team.leadCount} />
          <StatCard label="With an owner" value={owned} />
          <StatCard label="Waiting for an owner" value={team.unassignedLeadCount} />
        </StatsBar>
      </Card>

      <Card>
        <h2 className="font-display text-h3 text-onyx m-0">Roster capacity</h2>
        <p className="text-caption text-gray-mid m-0 mt-space-1 mb-space-3 max-w-reading">
          Counted by the person. <strong>Buyers held</strong> is the whole book each member is
          carrying across every team, so it is not this team&apos;s{" "}
          {team.leadCount} lead{team.leadCount === 1 ? "" : "s"} counted a second way and is
          not expected to match it.
        </p>
        <StatsBar columns={3}>
          <StatCard label="Members" value={team.memberCount} />
          <StatCard label="Consultants" value={team.specialistCount} />
          <StatCard label="Buyers held by members" value={team.activeBuyersHeldByMembers} />
        </StatsBar>
      </Card>
    </div>
  );
}
